/**
 * Backup and Restore Utilities
 * Cross-platform file operations for web and native (Android/iOS)
 */

import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { APP_CONFIG } from '../config/app.config';

/**
 * Download/save a JSON backup file
 * Web: Creates download link
 * Native: Saves to Cache directory and opens share sheet
 */
export async function downloadBackupFile(fileName: string, data: any): Promise<void> {
  const jsonString = JSON.stringify(data, null, 2);
  
  if (Capacitor.isNativePlatform()) {
    try {
      console.log('[Backup] Starting native backup...', { fileName, platform: Capacitor.getPlatform() });
      
      // Use Cache directory - doesn't require permissions on Android
      // Files in Cache can still be shared via Share API
      const result = await Filesystem.writeFile({
        path: fileName,
        data: jsonString,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });
      
      console.log('[Backup] File written successfully:', result.uri);
      
      // Check if Share is available
      const canShare = await Share.canShare();
      console.log('[Backup] Can share:', canShare);
      
      if (!canShare) {
        throw new Error('Share functionality is not available on this device');
      }
      
      // Open share sheet so user can save to desired location
      await Share.share({
        title: 'Save Backup File',
        text: `${APP_CONFIG.NAME} Backup`,  // Use app name from config
        url: result.uri,
        dialogTitle: 'Save your backup file',
      });
      
      console.log('[Backup] Share dialog shown successfully');
    } catch (error: any) {
      console.error('[Backup] Native backup failed:', error);
      console.error('[Backup] Error details:', {
        message: error?.message,
        stack: error?.stack,
        code: error?.code,
      });
      throw new Error(`Failed to create backup file: ${error?.message || error}`);
    }
  } else {
    // Web: Standard download
    const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

/**
 * Read a backup file
 * Web: Reads from File object
 * Native: This function is called after file picker, so same as web
 */
export async function readBackupFile(file: File): Promise<any> {
  const text = await file.text();
  return JSON.parse(text);
}

/**
 * Trigger file picker for restore
 * Returns a promise that resolves when user selects a file
 * Web: Uses input[type=file]
 * Native: Uses input[type=file] (works through Capacitor bridge)
 */
export function pickBackupFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0] || null;
      resolve(file);
    };
    
    input.oncancel = () => {
      resolve(null);
    };
    
    input.click();
  });
}
